import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import GachaWheel from './components/GachaWheel';
import MyItems from './components/MyItems';

interface GachaItem {
  id: string;
  name: string;
  description: string;
  image_url: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  min_level: number;
}

export default function LevelPage() {
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState<'gacha' | 'items'>('gacha');
  const [canGacha, setCanGacha] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      checkDailyGacha();
    }
  }, [user]);

  const checkDailyGacha = async () => {
    if (!user) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('user_daily_gacha')
        .select('*')
        .eq('user_id', user.id)
        .eq('last_gacha_date', today)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setCanGacha(!data);
    } catch (error) {
      console.error('뽑기 확인 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelBenefits = (level: number) => {
    const benefits = [
      '매일 1회 무료 뽑기',
      '기본 아이템 획득 가능',
    ];

    if (level >= 3) benefits.push('레어 아이템 획득 확률 증가');
    if (level >= 5) benefits.push('에픽 아이템 획득 가능');
    if (level >= 7) benefits.push('전설 아이템 획득 가능');
    if (level >= 10) benefits.push('모든 아이템 획득 확률 2배');

    return benefits;
  };

  if (!user || !profile) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">로그인이 필요합니다</h2>
            <p className="text-white/80 mb-8">회원등급 시스템을 이용하려면 로그인해주세요</p>
            <a
              href="/login"
              className="inline-block bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white px-8 py-3 rounded-lg font-extrabold hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
            >
              로그인하기
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-24">
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold text-white mb-2">회원등급 시스템</h1>
          <p className="text-white/80 font-semibold">매일 뽑기를 통해 특별한 아이템을 획득하세요</p>
        </div>

        {/* 현재 레벨 정보 */}
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-xl shadow-xl p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-white mb-2">현재 레벨</h2>
              <div className="flex items-center gap-4">
                <span className="text-6xl font-extrabold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 bg-clip-text text-transparent">
                  LV {profile.level}
                </span>
              </div>
            </div>
            <div className="w-32 h-32 flex items-center justify-center bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 rounded-full">
              <i className="ri-vip-crown-line text-6xl text-white"></i>
            </div>
          </div>

          <div className="border-t border-white/20 pt-6">
            <h3 className="text-lg font-extrabold text-white mb-4">레벨 혜택</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {getLevelBenefits(profile.level).map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <i className="ri-check-line text-cyan-400 text-xl"></i>
                  <span className="text-white/90 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 탭 전환 */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('gacha')}
            className={`px-6 py-3 rounded-lg font-extrabold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'gacha'
                ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white'
                : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            <i className="ri-gift-line mr-2"></i>
            매일 뽑기
          </button>
          <button
            onClick={() => setActiveTab('items')}
            className={`px-6 py-3 rounded-lg font-extrabold transition-all whitespace-nowrap cursor-pointer ${
              activeTab === 'items'
                ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white'
                : 'bg-white/10 text-white/80 hover:bg-white/20'
            }`}
          >
            <i className="ri-treasure-map-line mr-2"></i>
            내 아이템
          </button>
        </div>

        {/* 컨텐츠 */}
        {activeTab === 'gacha' ? (
          <GachaWheel 
            canGacha={canGacha} 
            userLevel={profile.level}
            onGachaComplete={checkDailyGacha}
          />
        ) : (
          <MyItems userId={user.id} />
        )}
      </div>

      <Footer />
    </div>
  );
}
